import React, { useEffect, useState } from "react";
import api from "../../services/api";
import cdrFilesService from '../../services/cdrFilesService';
import { Modal, Tab, Tabs } from "react-bootstrap";
import CdrFilesServerWarning from '../Common/CdrFilesServerWarning';

export default function ImportCdrFilesPage() {
  const [jobs, setJobs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [yandexDiskPath, setYandexDiskPath] = useState("");
  const [jobName, setJobName] = useState("");
  const [importType, setImportType] = useState("create");
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [whitePercent, setWhitePercent] = useState(0);
  const [varnishPercent, setVarnishPercent] = useState(0);
  const [autoQr, setAutoQr] = useState(false);
  const [vectorsToBitmap, setVectorsToBitmap] = useState(false);
  const [cutBitmaps, setCutBitmaps] = useState(false);
  const [dpiEnabled, setDpiEnabled] = useState(false);
  const [dpiValue, setDpiValue] = useState(500);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [activeTab, setActiveTab] = useState("zip");
  const [cdrFilesServerAvailable, setCdrFilesServerAvailable] = useState(false);

  const checkCdrFilesServerAvailable = async () => {
      try {
        const response = await cdrFilesService.health();
        setCdrFilesServerAvailable(true);
        return true;
      } catch (err) {
        console.error(err);
        setCdrFilesServerAvailable(false);
        return false;
      }
  };

  useEffect(() => {
    checkCdrFilesServerAvailable();
  }, []);
  
  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchJobs = async () => {
    if (checkCdrFilesServerAvailable() === false)
      return;
    
    const response = await cdrFilesService.get("/ImportCdrFiles/jobs");
    setJobs(response.data);
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleYandexDiskPathChange = (event) => {
    setYandexDiskPath(event.target.value);
  };

  const handleImport = async () => {
    if (activeTab === "zip" && !file) return;
    if (activeTab === "yadisk" && !yandexDiskPath) return;

    try {
      const queryParams = new URLSearchParams();
      queryParams.append("name", jobName);
      queryParams.append("autoQr", autoQr);
      queryParams.append('vectorsToBitmap', vectorsToBitmap);
      queryParams.append('whitePercent', whitePercent);
      queryParams.append('varnishPercent', varnishPercent);
      if (dpiEnabled) queryParams.append("dpi", dpiValue);
      queryParams.append("cutBitmaps", cutBitmaps);

      if (activeTab === "zip") {
        const formData = new FormData();
        formData.append("file", file);

        await cdrFilesService[importType === "create" ? "post" : "patch"](
          `/ImportCdrFiles/zip?${queryParams.toString()}`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress(percentCompleted);
            },
          }
        );
      } else {
        await cdrFilesService[importType === "create" ? "post" : "patch"](
          `/ImportCdrFiles/disk?${queryParams.toString()}`,
          { path: yandexDiskPath }
        );
      }

      resetForm();
      fetchJobs();
    } catch (error) {
      console.error("Ошибка импорта", error);
    }
  };

  const resetForm = () => {
    setIsModalOpen(false);
    setFile(null);
    setYandexDiskPath("");
    setJobName("");
    setWhitePercent(0);
    setVarnishPercent(0);
    setAutoQr(false);
    setVectorsToBitmap(false);
    setCutBitmaps(false);
    setDpiEnabled(false);
    setDpiValue(500);
    setUploadProgress(0);
  };

  const downloadFile = async (type, jobId) => {
    const response = await cdrFilesService.get(`/ImportCdrFiles/download/${jobId}/${type}`, {
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${type}_${jobId}.xlsx`);
    document.body.appendChild(link);
    link.click();
  };

  const abortJob = async (jobId) => {
    await cdrFilesService.post(`/ImportCdrFiles/abort/${jobId}`);
    fetchJobs();
  };

  const toggleExpand = (jobId) => {
    setExpandedJobId(expandedJobId === jobId ? null : jobId);
  };

  const statusLabels = {
    [-1]: "Ошибка",
    [0]: "Отменено",
    [1]: "Ожидание",
    [2]: "В процессе",
    [3]: "Завершено",
  };

  const getProgress = (job) => {
    if (job.totalCount === 0) return 0;
    return Math.round(((job.successCount + job.errorCount) / job.totalCount) * 100);
  };

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between mb-4">
        <h1 className="h3">Импорт макетов</h1>
        <button
          className="btn btn-primary"
          onClick={() => setIsModalOpen(true)}
          disabled={cdrFilesServerAvailable === false}
        >
          Новый импорт
        </button>
      </div>

      <CdrFilesServerWarning cdrFilesServerAvailable={cdrFilesServerAvailable} />
      
      <div className="row g-4">
        {jobs.map((job) => (
          <div key={job.id} className="col-12">
            <div className="card">
              <div
                className="card-body"
                style={{ cursor: "pointer" }}
                onClick={() => toggleExpand(job.id)}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="card-title mb-1">{job.name}</h5>
                    <small className="text-muted">
                      {new Date(job.creationTime).toLocaleString()}
                    </small>
                    <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
                      <span className="badge bg-secondary">
                        {job.action === 1 ? "Создание" : "Обновление"}
                      </span>
                      {job.autoQr && (
                        <span className="badge bg-success">AutoQR</span>
                      )}
                      {job.varnishPercent > 0 && (
                        <span className="badge bg-warning text-dark">
                          {job.varnishPercent}% лака
                        </span>
                      )}
                      {job.whitePercent > 0 && (
                        <span className="badge bg-light text-dark">
                          {job.whitePercent}% белого
                        </span>
                      )}
                      {job.dpi && (
                        <span className="badge bg-info text-dark">
                          {job.dpi} DPI
                        </span>
                      )}
                      {job.cutBitmaps && (
                        <span className="badge bg-secondary">Растры обрезаны</span>
                      )}
                    </div>
                  </div>
                  <div className="text-end">
                    <p className="mb-0">
                      <strong>Статус:</strong> {statusLabels[job.status]}
                    </p>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-md-3 col-6">
                    <small className="text-muted d-block">Создатель:</small>
                    <div>{job.createdBy}</div>
                  </div>
                  <div className="col-md-3 col-6">
                    <small className="text-muted d-block">Успешно:</small>
                    <div>{job.successCount}</div>
                  </div>
                  <div className="col-md-3 col-6 mt-2 mt-md-0">
                    <small className="text-muted d-block">Ошибки:</small>
                    <div>{job.errorCount}</div>
                  </div>
                  <div className="col-md-3 col-6 mt-2 mt-md-0">
                    <small className="text-muted d-block">Всего:</small>
                    <div>{job.totalCount}</div>
                  </div>
                </div>
              </div>

              {expandedJobId === job.id && (
                <>
                  <div className="card-body border-top">
                    <div className="progress my-2" style={{ height: "6px" }}>
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${getProgress(job)}%` }}
                        aria-valuenow={getProgress(job)}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      ></div>
                    </div>
                    <small className="text-muted">
                      {getProgress(job)}% выполнено
                    </small>
                  </div>
                  <div className="card-footer d-flex flex-wrap gap-2">
                    <button
                      className="btn btn-outline-success"
                      onClick={() => downloadFile("success", job.id)}
                    >
                      Скачать успешные
                    </button>
                    <button
                      className="btn btn-outline-danger"
                      onClick={() => downloadFile("error", job.id)}
                    >
                      Скачать ошибки
                    </button>
                    {(job.status === 1 || job.status === 2) && (
                      <button
                        className="btn btn-warning"
                        onClick={() => abortJob(job.id)}
                      >
                        Отменить импорт
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <Modal show={isModalOpen} onHide={resetForm} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Новый импорт макетов</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mt-3">
            <div className="mb-3">
              <label className="form-label">Название импорта</label>
              <input
                type="text"
                className="form-control"
                value={jobName}
                onChange={(e) => setJobName(e.target.value)}
              />
            </div>
            <div className="row justify-content-start mb-3">
              <label className="col-2 form-label">Тип импорта</label>
              <div className="col-2 row justify-content-start">
                <div className="col form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="importTypeCreate"
                    value="create"
                    checked={importType === "create"}
                    onChange={(e) => setImportType(e.target.value)}
                  />
                  <label className="form-check-label">Создание</label>
                </div>
                <div className="col form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="importTypeUpdate"
                    value="update"
                    checked={importType === "update"}
                    onChange={(e) => setImportType(e.target.value)}
                  />
                  <label className="form-check-label">Обновление</label>
                </div>
              </div>
            </div>
            
            <CommonFormElements />
            
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-3"
            >
              <Tab eventKey="zip" title="Zip">
                <div className="mb-3">
                    <label className="form-label">Файл ZIP</label>
                    <input
                      type="file"
                      accept=".zip"
                      onChange={handleFileChange}
                      className="form-control"
                    />
                  </div>
                  {uploadProgress > 0 && (
                    <div className="progress my-2">
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${uploadProgress}%` }}
                        aria-valuenow={uploadProgress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      ></div>
                    </div>
                  )}
              </Tab>
              <Tab eventKey="yadisk" title="ЯДиск">
                  <div className="mb-3">
                    <label className="form-label">Путь к папке на Яндекс.Диске</label>
                    <input
                      type="text"
                      className="form-control"
                      value={yandexDiskPath}
                      onChange={handleYandexDiskPathChange}
                      placeholder="Например: /Сервер/Макеты"
                    />
                  </div>
              </Tab>
            </Tabs>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={resetForm}>
            Отмена
          </button>
          <button className="btn btn-primary" onClick={handleImport}>
            Импортировать
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );

  function CommonFormElements() {
    return (
      <>
        <div class="row ms-2 justify-content-start">

          {/* Ползунок Процент белого */}
          <div className="mt-3">
            <label htmlFor="whitePercentRange" className="form-label">
              Процент белого: {whitePercent}%
            </label>
            <input
              type="range"
              className="form-range"
              id="whitePercentRange"
              min="0"
              max="100"
              step="5"
              value={whitePercent}
              onChange={(e) => setWhitePercent(parseInt(e.target.value))}
            />
          </div>

          {/* Ползунок Процент лака */}
          <div className="mt-3">
            <label htmlFor="varnishPercentRange" className="form-label">
              Процент лака: {varnishPercent}%
            </label>
            <input
              type="range"
              className="form-range"
              id="varnishPercentRange"
              min="0"
              max="100"
              step="5"
              value={varnishPercent}
              onChange={(e) => setVarnishPercent(parseInt(e.target.value))}
            />
          </div>

          <div className="mb-3 ms-3 form-check form-switch">
            <input
              type="checkbox"
              className="form-check-input"
              id="autoQr"
              checked={autoQr}
              onChange={(e) => setAutoQr(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="autoQr">
              Автоматически размещать QR
            </label>
          </div>
        </div>

        <div className="mb-3 ms-4 form-check form-switch">
          <input
            type="checkbox"
            className="form-check-input"
            id="vectorsToBitmap"
            checked={vectorsToBitmap}
            onChange={(e) => setVectorsToBitmap(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="vectorsToBitmap">
            Преобразовать вектора в растр
          </label>
        </div>

        <div className="mb-3 ms-4 form-check form-switch">
          <input
            type="checkbox"
            className="form-check-input"
            id="cutBitmaps"
            checked={cutBitmaps}
            onChange={(e) => setCutBitmaps(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="cutBitmaps">
            Обрезать растры по контуру
          </label>
        </div>
        
        <div className="d-flex align-items-center mb-3 ms-4">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="dpiToggle"
              checked={dpiEnabled}
              onChange={() => setDpiEnabled(!dpiEnabled)}
            />
            <label className="form-check-label" htmlFor="dpiToggle">
              Уменьшить DPI (точек на дюйм)
            </label>
          </div>
          <select
            className="form-select ms-3"
            value={dpiValue}
            onChange={(e) => setDpiValue(parseInt(e.target.value))}
            disabled={!dpiEnabled}
            style={{ width: "120px" }}
          >
            <option value={300}>300</option>
            <option value={400}>400</option>
            <option value={500}>500</option>
            <option value={600}>600</option>
          </select>
        </div>
        
      </>
    );
  }
}